import { useState } from 'react';
import {
  addDeviceOnlyMember,
  createInvite,
  getHousehold,
  leaveHousehold,
  removeMember,
  resendInvite,
  revokeInvite,
  setMonitoring,
  type DeviceOnlyMember,
  type HouseholdMember,
} from '../api/endpoints.js';
import { useAction, useApi, useIntentKey } from '../api/hooks.js';
import {
  focusCropStyle,
  memberDisplayName,
  memberInitials,
  pfpExpired,
} from '../flow/member-space.js';
import {
  Async,
  Badge,
  Bool,
  Button,
  Failure,
  Field,
  KeyValue,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

const BLANK_MEMBER: DeviceOnlyMember = {
  first_name: '',
  last_name: '',
  email: '',
  date_of_birth: '',
};

/**
 * The roster's profile picture, or initials.
 *
 * `pfp_url` is a presigned GET signed when the roster was READ, so it can
 * expire while this screen is open. `pfp_expires_at` is the authority on that,
 * not the TTL behind it, and a stale URL falls back to initials rather than
 * rendering a broken image. `null` is the ordinary state (P24).
 */
function MemberPfp({ member }: { member: HouseholdMember }) {
  if (member.pfp_url === null || pfpExpired(member.pfp_expires_at)) {
    return (
      <span className="pfp pfp-initials pfp-sm" aria-hidden="true">
        {memberInitials(member)}
      </span>
    );
  }
  const crop = focusCropStyle(member.pfp_focus);
  return (
    <img
      className="pfp pfp-sm"
      src={member.pfp_url}
      alt=""
      // The subject's OWN face box inside the picture. A pfp copied out of a
      // family shot may hold three people, and a centre crop picks whoever
      // stands in the middle.
      style={{ objectFit: crop.objectFit, objectPosition: crop.objectPosition }}
    />
  );
}

export function HouseholdScreen() {
  const household = useApi(getHousehold, []);
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [message, setMessage] = useState('');
  const [member, setMember] = useState<DeviceOnlyMember>(BLANK_MEMBER);

  const inviteKey = useIntentKey();
  const memberKey = useIntentKey();

  const reload = () => household.reload();
  const invite = useAction(createInvite, () => {
    inviteKey.next();
    reload();
  });
  const addMember = useAction(addDeviceOnlyMember, () => {
    memberKey.next();
    setMember(BLANK_MEMBER);
    reload();
  });
  const monitoring = useAction(setMonitoring, reload);
  const remove = useAction(removeMember, () => {
    setConfirmRemove(null);
    reload();
  });
  /**
   * REMOVING AN ON-DEVICE MEMBER DELETES THE PHOTOS THEY ARE THE SUBJECT OF.
   * They have no login, and once the seat is gone the manager cannot reach
   * those photos either, so leaving them would strand images nobody can see or
   * remove. That is destructive and irreversible, so it is confirmed here and
   * the confirmation NAMES what goes.
   */
  const [confirmRemove, setConfirmRemove] = useState<HouseholdMember | null>(null);
  const revoke = useAction(revokeInvite, reload);
  const resend = useAction(resendInvite, reload);
  const leave = useAction(leaveHousehold, reload);

  return (
    <Screen
      title="Household"
      lede="Seats, members, invites. Every action authorises on the session — no route here takes an invitee identifier in the body, which is exactly what the legacy accept endpoint got wrong."
    >
      <Async state={household} empty="no household — a plan with seats creates one">
        {(data) => (
          <>
            <Panel title="seats">
              <KeyValue
                rows={[
                  ['household_id', <code key="h">{data.household_id}</code>],
                  ['plan_code', data.plan_code],
                  ['seats_used', data.seats_used],
                  ['seat_limit', data.seat_limit],
                ]}
              />
            </Panel>

            <Panel title={`members · ${data.members.length}`}>
              {data.members.length === 0 ? (
                <p className="empty">no members</p>
              ) : (
                <div className="scroll-x">
                  <table>
                    <thead>
                      <tr>
                        <th>name</th>
                        <th>role</th>
                        <th>device</th>
                        <th>consent</th>
                        <th>enrolment</th>
                        <th>covered</th>
                        <th>hits</th>
                        <th>monitoring</th>
                        <th>photos</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.members.map((entry) => (
                        <tr key={entry.person_id}>
                          <td>
                            <div className="member-cell">
                              <MemberPfp member={entry} />
                              <div>
                                {memberDisplayName(entry)}
                                <div className="muted" style={{ fontSize: 12 }}>
                                  {entry.email ?? 'no email'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge>{entry.role}</Badge>
                          </td>
                          <td>
                            <Bool value={entry.has_own_device} />
                          </td>
                          <td>{entry.consent_status}</td>
                          <td>{entry.enrolment_status}</td>
                          <td>
                            <Bool value={entry.covered} />
                          </td>
                          <td>{entry.active_hits}</td>
                          <td>
                            <Bool value={entry.monitoring_enabled} />
                          </td>
                          <td>
                            {/* SERVER-COMPUTED. It already accounts for
                                has_own_device AND whether this caller owns the
                                household, so it is read, never re-derived: a
                                household MEMBER reading this roster sees the
                                others and must not be offered their photos. */}
                            {entry.photos_visible ? (
                              <Badge tone="ok">visible</Badge>
                            ) : (
                              <Badge>private</Badge>
                            )}
                          </td>
                          <td>
                            <Row>
                              <a
                                className="action secondary"
                                href={`#/member/${encodeURIComponent(entry.person_id)}`}
                              >
                                Open
                              </a>
                              <Button
                                variant="secondary"
                                onClick={() =>
                                  monitoring.run(entry.person_id, !entry.monitoring_enabled)
                                }
                                disabled={monitoring.pending}
                              >
                                {entry.monitoring_enabled ? 'Pause' : 'Resume'}
                              </Button>
                              <Button
                                variant="danger"
                                onClick={() => setConfirmRemove(entry)}
                                disabled={remove.pending}
                              >
                                Remove
                              </Button>
                            </Row>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {confirmRemove !== null && (
                <Note>
                  <strong>Remove {memberDisplayName(confirmRemove)}?</strong>{' '}
                  {confirmRemove.has_own_device ? (
                    <>
                      Their seat goes and monitoring stops for them. They keep their account and
                      their photos move with them &mdash; nothing of theirs is deleted here.
                    </>
                  ) : (
                    <>
                      Their seat goes <strong>and so do the photos in their space</strong>. They
                      have no login of their own, so once the seat is gone nobody can reach those
                      photos &mdash; leaving them would strand images nobody can see or remove. Your
                      own photos are untouched, including family shots they happen to appear in.
                      This cannot be undone.
                    </>
                  )}
                  <div style={{ marginTop: 10 }}>
                    <Row>
                      <Button
                        variant="danger"
                        onClick={() => remove.run(confirmRemove.person_id)}
                        disabled={remove.pending}
                      >
                        {remove.pending
                          ? 'removing…'
                          : confirmRemove.has_own_device
                            ? 'Remove the seat'
                            : 'Remove the seat and delete their photos'}
                      </Button>
                      <Button variant="secondary" onClick={() => setConfirmRemove(null)}>
                        Keep them
                      </Button>
                    </Row>
                  </div>
                </Note>
              )}
              {monitoring.error !== null && <Failure error={monitoring.error} />}
              {remove.error !== null && (
                <Failure
                  error={remove.error}
                  hint={
                    remove.error.code === 'OWNER_CANNOT_LEAVE'
                      ? 'The owner cannot be removed from their own household.'
                      : undefined
                  }
                />
              )}
            </Panel>

            <Panel title={`pending invites · ${data.pending_invites.length}`}>
              {data.pending_invites.length === 0 ? (
                <p className="empty">none outstanding</p>
              ) : (
                <div className="scroll-x">
                  <table>
                    <thead>
                      <tr>
                        <th>invite_id</th>
                        <th>email</th>
                        <th>created</th>
                        <th>expires</th>
                        <th>sends</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.pending_invites.map((entry) => (
                        <tr key={entry.invite_id}>
                          <td>
                            <code>{entry.invite_id}</code>
                          </td>
                          <td>{entry.invitee_email ?? <span className="muted">by phone</span>}</td>
                          <td>{entry.created_at}</td>
                          <td>{entry.expires_at}</td>
                          <td>
                            {entry.send_count}
                            {entry.last_sent_at !== null && (
                              <div className="muted" style={{ fontSize: 12 }}>
                                {entry.last_sent_at}
                              </div>
                            )}
                          </td>
                          <td>
                            <Row>
                              <Button
                                variant="secondary"
                                onClick={() => resend.run(entry.invite_id)}
                                disabled={resend.pending}
                              >
                                Resend
                              </Button>
                              <Button
                                variant="danger"
                                onClick={() => revoke.run(entry.invite_id)}
                                disabled={revoke.pending}
                              >
                                Revoke
                              </Button>
                            </Row>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {resend.error !== null && <Failure error={resend.error} />}
              {revoke.error !== null && <Failure error={revoke.error} />}
            </Panel>

            <Raw label="GET /v1/household" value={data} />
          </>
        )}
      </Async>

      <Panel title="invite someone with their own device">
        <Row>
          <Field label="invitee_phone_e164" value={invitePhone} onChange={setInvitePhone} />
          <Field label="invitee_email" value={inviteEmail} onChange={setInviteEmail} />
          <Field label="message (optional)" value={message} onChange={setMessage} />
          <Button
            onClick={() =>
              invite.run(
                {
                  ...(invitePhone.length > 0 ? { invitee_phone_e164: invitePhone } : {}),
                  ...(inviteEmail.length > 0 ? { invitee_email: inviteEmail } : {}),
                  ...(message.length > 0 ? { message } : {}),
                },
                inviteKey.key,
              )
            }
            disabled={invite.pending || (invitePhone.length === 0 && inviteEmail.length === 0)}
          >
            {invite.pending ? 'inviting…' : 'Send invite'}
          </Button>
        </Row>
        <Note>
          One of phone or email is required. On dev, an SMS invite needs{' '}
          <code>SMS_PROVIDER=twilio</code> and an email invite cannot arrive at all —{' '}
          <code>EMAIL_PROVIDER=fake</code>.
        </Note>
        {invite.error !== null && (
          <Failure
            error={invite.error}
            hint={
              {
                HOUSEHOLD_FULL: 'Every seat on the plan is taken.',
                INVITE_LIMIT_REACHED:
                  'Too many invites are outstanding. Revoke one, or wait for a reply.',
                INVITE_PENDING: 'An invite to this person is already outstanding.',
                ALREADY_IN_HOUSEHOLD: 'They already hold a seat here.',
                HOUSEHOLD_PLAN_REQUIRED: 'Invites need a household-tier plan.',
              }[invite.error.code]
            }
          />
        )}
        {invite.result?.warnings !== undefined && invite.result.warnings.length > 0 && (
          <Note>
            <strong>Sent, with a caveat:</strong>{' '}
            {invite.result.warnings.map((warning) => warning.message).join(' ')}
          </Note>
        )}
        {invite.result !== null && <Raw label="invite response" value={invite.result} />}
      </Panel>

      <Panel title="add a device-only member">
        <p className="muted">
          No phone field of any shape: a device-only member has no device, and{' '}
          <code>persons.account_id IS NULL</code> is the whole mechanism.
        </p>
        <div className="row">
          <Field
            label="first_name"
            value={member.first_name}
            onChange={(value) => setMember({ ...member, first_name: value })}
          />
          <Field
            label="last_name"
            value={member.last_name}
            onChange={(value) => setMember({ ...member, last_name: value })}
          />
          <Field
            label="email (blank for a minor — their guardian signs)"
            value={member.email ?? ''}
            onChange={(value) => setMember({ ...member, email: value })}
          />
          <Field
            label="date_of_birth (YYYY-MM-DD)"
            value={member.date_of_birth}
            onChange={(value) => setMember({ ...member, date_of_birth: value })}
          />
          <Button
            onClick={() => {
              // An EMPTY email is not an absent one: zod rejects '' as an
              // address, so a blank field has to be omitted rather than sent.
              // Blank is how a minor is added here — their guardian signs, so
              // they need no address of their own.
              const { email, ...rest } = member;
              const trimmed = email?.trim() ?? '';
              addMember.run(
                trimmed === '' ? rest : { ...rest, email: trimmed },
                memberKey.key,
              );
            }}
            disabled={addMember.pending}
          >
            {addMember.pending ? 'adding…' : 'Add member'}
          </Button>
        </div>
        {addMember.error !== null && (
          <Failure
            error={addMember.error}
            hint={
              {
                HOUSEHOLD_FULL: 'Every seat on the plan is taken.',
                EMAIL_IN_USE: 'That email already belongs to a person.',
                UNDER_MIN_AGE: 'Below the age floor for a member.',
                GUARDIANSHIP_REQUIRED: 'A minor needs a guardian who can sign consent.',
              }[addMember.error.code]
            }
          />
        )}
        {addMember.result !== null && <Raw label="member response" value={addMember.result} />}
      </Panel>

      <Panel title="leave this household">
        <p className="muted">
          Unilateral by construction — it reads your own membership and nothing about the owner, and
          there is no route that re-adds someone.
        </p>
        <Row>
          <Button variant="danger" onClick={() => leave.run()} disabled={leave.pending}>
            {leave.pending ? 'leaving…' : 'Leave household'}
          </Button>
        </Row>
        {leave.error !== null && (
          <Failure
            error={leave.error}
            hint={
              leave.error.code === 'OWNER_CANNOT_LEAVE'
                ? 'The owner cannot leave. To end the plan, cancel it in the store (manage_url on the Plans screen); members can leave at any time.'
                : undefined
            }
          />
        )}
      </Panel>
    </Screen>
  );
}
